import React, { useRef, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Camera, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

interface SimplePhotoUploaderProps {
  onFileAdded: (file: File) => void;
}

const SimplePhotoUploader: React.FC<SimplePhotoUploaderProps> = ({ onFileAdded }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error("That photo's a bit too big — please choose one under 5MB.");
      return;
    }

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("Please select a valid image file (JPEG, PNG, or GIF)");
      return;
    }

    onFileAdded(file);
    
    // Show success state
    setIsSuccess(true);
    setTimeout(() => setIsSuccess(false), 2000);
    
    // Reset the input for consistent behavior
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        className="hidden"
      />
      <Button
        type="button"
        variant="outline"
        onClick={handleButtonClick}
        className={`border-[#CCCCCC] hover:bg-[#E8F0F8] transition-all duration-300 flex-1 ${
          isSuccess ? 'bg-[#E8F0F8] border-green-200 text-green-600' : ''
        }`}
      >
        {isSuccess ? (
          <>
            <CheckCircle2 className="mr-2 h-4 w-4" />
            Photo Added
          </>
        ) : (
          <>
            <Camera className="mr-2 h-4 w-4" />
            Add Photo
          </>
        )}
      </Button>
    </>
  );
};

export default SimplePhotoUploader;
